import type {
  AnalyticsQuery,
  AnalyticsQueryFilters,
} from "schemas/generated/analyticsRead";

import { minutesToMilliseconds } from "date-fns";
import { currencySchema } from "schemas/project";
import * as z from "zod";

export const DEFAULT_MAIN_RANGE_ID = "f13ffb7f-ea60-474c-a3dd-669070b5f8a2";
export const DEFAULT_COMPARE_AT_RANGE_ID =
  "a24a44db-3d1b-4ad7-a7c5-de1da66cad95";

export const metricNameSchema = z
  .enum([
    "conversions",
    "views",
    "unique_sessions",
    "conversion_rates",
    "revenue",
    "revenue_per_session",
    "average_order_value",
  ])
  .describe("MetricName");

/**
 * --------------------------------
 * Schemas for AnalyticsResponse
 * --------------------------------
 */

const rangeResultMetadataSchema = z.object({
  elementId: z.uuid().nullable(),
  title: z.string(),
  url: z.string(),
});

const rangeResultMetricsSchema = z.record(
  metricNameSchema,
  z.array(z.number()),
);

export const rangeResultSchema = z
  .object({
    urlPath: z.string(),
    metadata: rangeResultMetadataSchema,
    rangeId: z.uuid(),
    metrics: rangeResultMetricsSchema,
  })
  .describe("RangeResult");

const totalRowsCountSchema = z.number().min(0);

export const analyticsResponseSchema = z
  .object({
    totalRowsCount: totalRowsCountSchema,
    rangeResults: z.array(rangeResultSchema),
  })
  .describe("AnalyticsResponse");

/**
 * --------------------------------
 * Schemas for AnalyticsQuery
 * --------------------------------
 */

const urlHostsSchema = z.array(z.string());
const offsetSchema = z.number().min(0);
const limitSchema = z.number().positive().max(100);

export const orderSchema = z.enum(["ASC", "DESC"]).describe("Order");

export const pageTypeSchema = z
  .enum(["allPages", "entryPages"])
  .describe("PageType");

export const conditionOperatorSchema = z
  .enum(["equals", "does_not_equal", "contains", "does_not_contain"])
  .describe("ConditionOperator");

export const filterConditionSchema = z
  .discriminatedUnion("operator", [
    z.object({
      operator: z.literal("equals"),
      value: z.array(z.string()),
    }),
    z.object({
      operator: z.literal("does_not_equal"),
      value: z.array(z.string()),
    }),
    z.object({
      operator: z.literal("contains"),
      value: z.string(),
    }),
    z.object({
      operator: z.literal("does_not_contain"),
      value: z.string(),
    }),
  ])
  .describe("FilterCondition");

export const filterConditionsSchema = z
  .array(filterConditionSchema)
  .describe("FilterConditions");

export const urlParamsSchema = z
  .record(z.string(), filterConditionsSchema)
  .describe("QueryFiltersUrlParams");

export const filterSchema = z
  .object({
    urlPath: filterConditionsSchema,
    pageType: z.enum(["allPages", "entryPages"]).describe("PageType"),
    urlParams: urlParamsSchema,
  })
  .describe("AnalyticsQueryFilters");

export const queryRangeSchema = z
  .object({
    id: z.uuid(),
    startDatetime: z.number().min(0),
    endDatetime: z.number().positive(),
    interval: z.number().min(10),
  })
  .describe("QueryRange");

export const adjustedRangeSchema = queryRangeSchema
  .superRefine((input, ctx) => {
    const startDatetime = input.startDatetime;
    const endDatetime = input.endDatetime;
    const interval = input.interval;

    if (endDatetime <= startDatetime) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "range.endDatetime must be after range.startDatetime",
        path: ["sort", "endDatetime"],
      });
    }

    if (interval > endDatetime - startDatetime) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message:
          "Interval time must be shorter than the time between range.startDatetime and range.endDatetime",
        path: ["range", "interval"],
      });
    }

    if ((endDatetime - startDatetime) % interval !== 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message:
          "Interval doesn't fit into the range: range.endDatetime must be aligned with (i.e. a multiple of) the interval from range.startDatetime",
        path: ["range", "interval"],
      });
    }

    if (interval % minutesToMilliseconds(10) !== 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Interval must be a multiple of 10 minutes",
        path: ["range", "interval"],
      });
    }
  })
  .describe("AdjustedRange");

export const queryRangesSchema = z
  .object({
    mainRange: queryRangeSchema,
    compareAtRanges: z.array(queryRangeSchema),
  })
  .describe("QueryRanges");

export const analyticsQuerySchema = z
  .object({
    /**
     * Workspace is always required. This is how we
     * check the individual paths to be sure we're looking up the correct ones for the workspace.
     */
    urlHosts: urlHostsSchema.optional(),
    /**
     * The metrics that are directly stored in ClickHouse's timeseries_records table, and/or metrics
     * that need to be calculated from metrics that are stored in ClickHouse. E.g. "conversions",
     * "views", "conversion_rates".
     */
    metrics: z.array(metricNameSchema).min(1),
    /**
     * Sorting order for the sorting metric.
     */
    order: orderSchema,
    /**
     * Sort based on a given metric name.
     */
    sortMetric: metricNameSchema,
    /**
     * The range being considered, including the interval (in MINUTES) of the buckets that we want to retrieve. E.g. if interval = 30, then we're
     * aggregating the metric calculation in buckets of 30min.
     */
    ranges: queryRangesSchema,
    /**
     * Offset used in pagination.
     */
    offset: offsetSchema,
    /**
     * Limit of the results per page.
     */
    limit: limitSchema,
    filters: filterSchema,
    targetCurrency: currencySchema.nullish(),
    mode: z
      .enum(["groupByUrlPath", "groupByDomain", "funnel"])
      .prefault("groupByUrlPath"),
    isFractional: z.boolean().prefault(false),
  })
  .superRefine((input, ctx) => {
    if (!input.metrics.includes(input.sortMetric)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Sort value must be one of of the specified metrics.",
        path: ["sort"],
      });
    }
  })
  .describe("AnalyticsQuery");

export type AnalyticsQueryWithoutRanges = Omit<AnalyticsQuery, "ranges">;

/**
 * NOTE (Max, 2024-09-23): Filters that are used in sql.ts to filter out individual
 * events, regardless of the pageType (entryPages | allPages). We're not including
 * pageType as this one is used to alter the construction of CTEs, whereas the
 * SQL WHERE clause for the other filters remains the same, regardless of pageType.
 */
export type QueryFiltersRoot = Pick<AnalyticsQueryFilters, "urlPath">;

/**
 * --------------------------------
 * Schemas for retrieving urlParams
 * --------------------------------
 */

export const findUrlParamsQuerySchema = z
  .object({
    urlHosts: urlHostsSchema,
    order: orderSchema,
    offset: offsetSchema,
    limit: limitSchema,
    key: z.string(),
    value: z.string(),
  })
  .describe("FindUrlParamsQuery");

export const findUrlParamsResponseSchema = z
  .object({
    urlParams: z.array(z.string()),
  })
  .describe("FindUrlParamsResponse");

export const findSubDomainsForUrlHostsResponseSchema = z
  .array(
    z.object({
      urlHost: z.string(),
      isRootDomain: z.boolean(),
    }),
  )
  .describe("FindSubDomainsForUrlHostsResponse");

/**
 * --------------------------------
 * Public analytics SQL query contract
 * --------------------------------
 */

type AnalyticsQueryColumn = {
  name: string;
  type: string;
  public: boolean;
};

type AnalyticsQueryTable = {
  name: string;
  description: string;
  projectScoped: boolean;
  rowPolicySource?: string;
  public: boolean;
  /** Column each row is dated by (`date` on the event tables, `day` on the rollups), or null for tables with no time series. */
  timestampColumn: string | null;
  columns: AnalyticsQueryColumn[];
};

export const analyticsQueryLimits = {
  maxExecutionSeconds: 60,
  maxResultRows: 100_000,
} as const;

export const analyticsQueryGuidance = [
  "Use SELECT or WITH queries only.",
  "Do not add project or namespace filters; project scoping is enforced server-side.",
  "Use session_attribution(since, until) for cross-project session and attributed revenue; stored computed metrics and page rollups are project-local.",
  "Session revenue repeats across visited pages: deduplicate by subject for totals. Attributed revenue splits credit across eligible pageviews before domain filters.",
] as const;

export const analyticsQueryTables = [
  {
    name: "session_attribution",
    description:
      "Parameterized view: session_attribution(since='YYYY-MM-DD', until='YYYY-MM-DD'), inclusive UTC dates. Computes USD purchase, session, and equal-pageview attributed revenue across authorized sibling projects within that window, before domain filters. Cart/checkout views receive no attribution. Deduplicate sessionRevenueUsd by subject before summing. Inherits source row policies.",
    projectScoped: true,
    rowPolicySource: "events_computed",
    public: true,
    timestampColumn: "date",
    columns: [
      { name: "namespace", type: "String", public: false },
      { name: "subject", type: "UUID", public: true },
      { name: "date", type: "DateTime64(3)", public: true },
      { name: "id", type: "UUID", public: false },
      { name: "name", type: "LowCardinality(String)", public: true },
      { name: "data", type: "String", public: false },
      { name: "domain", type: "String", public: true },
      { name: "path", type: "String", public: true },
      { name: "title", type: "String", public: true },
      { name: "purchaseRevenueUsd", type: "Float64", public: true },
      { name: "sessionRevenueUsd", type: "Float64", public: true },
      { name: "sessionPurchaseCount", type: "UInt64", public: true },
      { name: "attributedRevenueUsd", type: "Float64", public: true },
    ],
  },
  {
    name: "events_computed",
    description:
      "Raw events with project-local computed session metrics. Use session_attribution for revenue across linked projects.",
    projectScoped: true,
    public: true,
    timestampColumn: "date",
    columns: [
      { name: "namespace", type: "String", public: false },
      { name: "subject", type: "UUID", public: true },
      { name: "date", type: "DateTime64(3)", public: true },
      { name: "id", type: "UUID", public: false },
      { name: "name", type: "LowCardinality(String)", public: true },
      { name: "data", type: "String", public: false },
      { name: "computed", type: "String", public: false },
      { name: "computedVersion", type: "UInt8", public: false },
      { name: "computedDate", type: "DateTime64(3)", public: false },
      { name: "insertDate", type: "DateTime64(3)", public: false },
      { name: "path", type: "String", public: true },
      { name: "domain", type: "String", public: true },
      { name: "currencyCode", type: "String", public: true },
      { name: "subjectPurchaseSum", type: "Float64", public: true },
      { name: "subjectPurchaseCount", type: "Float64", public: true },
      { name: "subjectPurchaseFraction", type: "Float64", public: true },
      { name: "subjectPurchaseFractionalSum", type: "Float64", public: true },
      { name: "title", type: "String", public: true },
    ],
  },
  {
    name: "namespace_to_domain",
    description: "Maps project analytics namespaces to domains.",
    projectScoped: true,
    public: false,
    timestampColumn: null,
    columns: [
      { name: "namespace", type: "String", public: false },
      { name: "domain", type: "String", public: false },
    ],
  },
  {
    name: "currency_exchange_rates",
    description: "Currency exchange rates for multi-currency reporting.",
    projectScoped: false,
    public: true,
    timestampColumn: "date",
    columns: [
      { name: "date", type: "Date", public: true },
      { name: "code", type: "String", public: true },
      { name: "rate", type: "Float32", public: true },
    ],
  },
  {
    name: "daily_page_rollups",
    description:
      "Pre-aggregated daily page metrics by day, domain, path, and UTM source. Revenue/conversion attribution is project-local; use session_attribution across linked projects.",
    projectScoped: true,
    public: true,
    timestampColumn: "day",
    columns: [
      { name: "day", type: "Date", public: true },
      { name: "namespace", type: "String", public: false },
      { name: "domain", type: "String", public: true },
      { name: "path", type: "String", public: true },
      { name: "utm_source", type: "String", public: true },
      { name: "views_state", type: "AggregateFunction(count)", public: true },
      {
        name: "unique_sessions_state",
        type: "AggregateFunction(uniq, UUID)",
        public: true,
      },
      {
        name: "converting_sessions_state",
        type: "AggregateFunction(uniq, UUID)",
        public: true,
      },
      {
        name: "fractional_conversions_state",
        type: "AggregateFunction(sum, Float64)",
        public: true,
      },
      {
        name: "fractional_revenue_usd_state",
        type: "AggregateFunction(sum, Float64)",
        public: true,
      },
      {
        name: "session_conversions_state",
        type: "AggregateFunction(sum, UInt64)",
        public: true,
      },
      {
        name: "session_revenue_usd_state",
        type: "AggregateFunction(sum, Float64)",
        public: true,
      },
    ],
  },
  {
    name: "daily_namespace_rollups",
    description:
      "Pre-aggregated daily project traffic metrics by day and UTM source.",
    projectScoped: true,
    public: true,
    timestampColumn: "day",
    columns: [
      { name: "day", type: "Date", public: true },
      { name: "namespace", type: "String", public: false },
      { name: "utm_source", type: "String", public: true },
      { name: "views_state", type: "AggregateFunction(count)", public: true },
      {
        name: "unique_sessions_state",
        type: "AggregateFunction(uniq, UUID)",
        public: true,
      },
      {
        name: "converting_sessions_state",
        type: "AggregateFunction(uniq, UUID)",
        public: true,
      },
    ],
  },
  {
    name: "daily_namespace_purchase_rollups",
    description: "Pre-aggregated daily purchase metrics by day and UTM source.",
    projectScoped: true,
    public: true,
    timestampColumn: "day",
    columns: [
      { name: "day", type: "Date", public: true },
      { name: "namespace", type: "String", public: false },
      { name: "utm_source", type: "String", public: true },
      {
        name: "purchase_count_state",
        type: "AggregateFunction(count)",
        public: true,
      },
      {
        name: "total_revenue_usd_state",
        type: "AggregateFunction(sum, Float64)",
        public: true,
      },
      {
        name: "unique_purchasing_sessions_state",
        type: "AggregateFunction(uniq, UUID)",
        public: true,
      },
    ],
  },
  {
    name: "benchmark_comparison",
    description:
      "Parameterized view: benchmark_comparison(window_days=1|7|30|90, since='YYYY-MM-DD', until='YYYY-MM-DD', channel='all'|'affiliate'|'email_sms'|'ai_assistant'|'paid_social'|'paid_search'|'organic_social'|'other_tagged'|'organic_search'|'direct'|'referral', device='all'|'mobile'|'tablet'|'desktop', by_location=0|1, by_price_tier=0|1). One row per day and metric: this project's own value over the window next to anonymized peers in the deepest cohort that passes (10+ brands, not low confidence). It keeps the asked-for axes and the narrowest category it can, trying country, then sub-region, then region at each category level, then drops price tier, then location. Price tiers are p0_25, p25_50, p50_75, p75_100: quartiles of peer brands' 90-day AOV in the root category; a brand with under 30 orders in 90 days has none. A brand in the top 10% or top 1% is compared within p90_100 or p99_100 when that band has enough brands. Metrics: sessions, pages_per_session, add_to_cart_rate, checkout_rate, conversion_rate, orders, revenue_usd, aov_usd, revenue_per_session_usd, and, only when channel='all' and device='all', the traffic shares channel_share_<channel> (one per channel above) and device_share_<device> (mobile, tablet, desktop): the share of this brand's sessions from that channel or device, next to peers' own shares, where a peer with none counts as 0. They need no checkout data. A brand with no category and no orders is compared on sessions, pages_per_session and the shares with all brands in every vertical (comparison_level 'all brands'), and on nothing else. days_covered is how many of the window's days have data: fewer than window_days means history is shorter than the window or the project had no sessions on some days. suspected_bot_sessions is how many sessions were taken out of my_value as suspected bot traffic (a channel and device with 3 times its usual sessions that view one page and don't add to cart or buy); peers are cleaned the same way, and Insights still counts these sessions. Peer columns are NULL when no cohort passes. Inherits source row policies.",
    projectScoped: true,
    rowPolicySource: "daily_namespace_benchmark_rollups",
    public: true,
    timestampColumn: "date",
    columns: [
      { name: "date", type: "Date", public: true },
      { name: "metric", type: "String", public: true },
      { name: "window_days", type: "UInt16", public: true },
      { name: "days_covered", type: "UInt16", public: true },
      { name: "suspected_bot_sessions", type: "UInt64", public: true },
      { name: "channel", type: "String", public: true },
      { name: "device", type: "String", public: true },
      { name: "my_value", type: "Nullable(Float64)", public: true },
      { name: "peer_blended", type: "Nullable(Float64)", public: true },
      { name: "peer_p25", type: "Nullable(Float64)", public: true },
      { name: "peer_median", type: "Nullable(Float64)", public: true },
      { name: "peer_p75", type: "Nullable(Float64)", public: true },
      { name: "peer_p90", type: "Nullable(Float64)", public: true },
      { name: "peer_brands", type: "UInt32", public: true },
      { name: "confidence", type: "String", public: true },
      { name: "compared_category", type: "String", public: true },
      { name: "compared_location", type: "String", public: true },
      { name: "compared_price_tier", type: "String", public: true },
      { name: "comparison_level", type: "String", public: true },
      { name: "my_price_tier", type: "String", public: true },
      { name: "my_aov_90d_usd", type: "Float64", public: true },
      { name: "my_location", type: "String", public: true },
    ],
  },
  {
    name: "benchmark_trend",
    description:
      "Parameterized view: benchmark_trend(since, until, channel, device, by_location, by_price_tier, ma_days, ema_days). benchmark_comparison over 1-day windows plus moving averages per metric: my_sma and peer_sma over the last ma_days days, my_ema and peer_ema with an ema_days half-life. Days without a value are skipped, not counted as 0. Inherits source row policies.",
    projectScoped: true,
    rowPolicySource: "daily_namespace_benchmark_rollups",
    public: true,
    timestampColumn: "date",
    columns: [
      { name: "date", type: "Date", public: true },
      { name: "metric", type: "String", public: true },
      { name: "my_value", type: "Nullable(Float64)", public: true },
      { name: "peer_blended", type: "Nullable(Float64)", public: true },
      { name: "peer_median", type: "Nullable(Float64)", public: true },
      { name: "peer_p25", type: "Nullable(Float64)", public: true },
      { name: "peer_p75", type: "Nullable(Float64)", public: true },
      { name: "peer_brands", type: "UInt32", public: true },
      { name: "compared_category", type: "String", public: true },
      { name: "my_sma", type: "Nullable(Float64)", public: true },
      { name: "peer_sma", type: "Nullable(Float64)", public: true },
      { name: "my_ema", type: "Nullable(Float64)", public: true },
      { name: "peer_ema", type: "Nullable(Float64)", public: true },
    ],
  },
  {
    name: "daily_category_benchmarks",
    description:
      "Anonymized daily peer statistics per cohort, readable across projects. One row per snapshot_date, window_days (1, 7, 30, 90), vertical (ecommerce, saas, services, or all: every vertical together, traffic metrics only, with no categories or price tiers), category_key (an industry path such as 'Home & Garden > Kitchen & Dining', or '' for the whole vertical), channel, device (mobile, tablet, desktop), location_key (the brand's home location (location path such as '/Europe/Western Europe/Germany', or '' for everywhere), price_tier (p0_25, p25_50, p50_75, p75_100, plus the overlapping top bands p90_100 and p99_100) and metric; 'all' (or '') on an axis means not cut by it. blended is pooled across brands and one large brand can move it; p25 to p90 are brand-level quantiles. Rows need 10 brands. Traffic shares (channel_share_<channel>, device_share_<device>) are on channel='all', device='all' rows only. confidence is high, medium or low from the relative standard error, or for shares from the standard error in share points: never show low rows to customers. Query with FINAL.",
    projectScoped: false,
    public: true,
    timestampColumn: "snapshot_date",
    columns: [
      { name: "snapshot_date", type: "Date", public: true },
      { name: "window_days", type: "UInt16", public: true },
      { name: "vertical", type: "LowCardinality(String)", public: true },
      { name: "category_level", type: "UInt8", public: true },
      { name: "category_key", type: "String", public: true },
      { name: "channel", type: "LowCardinality(String)", public: true },
      { name: "device", type: "LowCardinality(String)", public: true },
      { name: "location_level", type: "UInt8", public: true },
      { name: "location_key", type: "String", public: true },
      { name: "price_tier", type: "LowCardinality(String)", public: true },
      { name: "metric", type: "LowCardinality(String)", public: true },
      { name: "brands", type: "UInt32", public: true },
      { name: "blended", type: "Float64", public: true },
      { name: "p25", type: "Float64", public: true },
      { name: "p50", type: "Float64", public: true },
      { name: "p75", type: "Float64", public: true },
      { name: "p90", type: "Nullable(Float64)", public: true },
      { name: "mean", type: "Float64", public: true },
      { name: "stddev", type: "Float64", public: true },
      { name: "rse", type: "Float64", public: true },
      { name: "confidence", type: "LowCardinality(String)", public: true },
      { name: "computed_at", type: "DateTime", public: false },
    ],
  },
  {
    name: "daily_namespace_benchmark_profiles",
    description:
      "This project's brand (root domain) and its value on every benchmark axis per day: vertical, category_path, location_path, 90-day AOV and orders, price tier, top bands (p90_100, p99_100) it's in, and its root category's tier boundaries (tier_p25_usd to tier_p99_usd, NULL until the category has enough priced peers). Query with FINAL.",
    projectScoped: true,
    public: true,
    timestampColumn: "snapshot_date",
    columns: [
      { name: "snapshot_date", type: "Date", public: true },
      { name: "namespace", type: "String", public: false },
      { name: "brand", type: "String", public: true },
      { name: "vertical", type: "LowCardinality(String)", public: true },
      { name: "category_path", type: "Array(String)", public: true },
      { name: "country_code", type: "LowCardinality(String)", public: true },
      { name: "location_path", type: "Array(String)", public: true },
      { name: "aov_90d_usd", type: "Float64", public: true },
      { name: "orders_90d", type: "UInt64", public: true },
      { name: "price_tier", type: "LowCardinality(String)", public: true },
      { name: "top_bands", type: "Array(String)", public: true },
      { name: "tier_p25_usd", type: "Nullable(Float64)", public: true },
      { name: "tier_p50_usd", type: "Nullable(Float64)", public: true },
      { name: "tier_p75_usd", type: "Nullable(Float64)", public: true },
      { name: "tier_p90_usd", type: "Nullable(Float64)", public: true },
      { name: "tier_p99_usd", type: "Nullable(Float64)", public: true },
      { name: "is_peer", type: "UInt8", public: false },
    ],
  },
  {
    name: "benchmark_brand_metrics",
    description:
      "Parameterized view: benchmark_brand_metrics(start='YYYY-MM-DD', end='YYYY-MM-DD'). This project's brand totals per as_of day, 1/7/30/90-day window, channel and device ('all' included on both), as used for its benchmarks. days_covered is how many of the window's days have data. Suspected bot traffic is taken out of sessions and page_views, and suspected_bot_sessions counts it. Prefer benchmark_comparison. Inherits source row policies.",
    projectScoped: true,
    rowPolicySource: "daily_namespace_benchmark_rollups",
    public: true,
    timestampColumn: "as_of",
    columns: [
      { name: "as_of", type: "Date", public: true },
      { name: "window_days", type: "UInt16", public: true },
      { name: "days_covered", type: "UInt16", public: true },
      { name: "brand", type: "String", public: true },
      { name: "channel", type: "String", public: true },
      { name: "device", type: "String", public: true },
      { name: "sessions", type: "UInt64", public: true },
      { name: "page_views", type: "UInt64", public: true },
      { name: "add_to_cart_sessions", type: "UInt64", public: true },
      { name: "checkout_sessions", type: "UInt64", public: true },
      { name: "purchase_sessions", type: "UInt64", public: true },
      { name: "orders", type: "UInt64", public: true },
      { name: "revenue_usd", type: "Float64", public: true },
      { name: "suspected_bot_sessions", type: "UInt64", public: true },
    ],
  },
  {
    name: "daily_namespace_benchmark_rollups",
    description:
      "This project's daily benchmark inputs by session channel and device, built from each session 3 hours after it starts (bots excluded). Each session is on exactly one row, so sum() the counts across rows and days. revenue_usd is converted at the purchase day's rate.",
    projectScoped: true,
    public: true,
    timestampColumn: "day",
    columns: [
      { name: "day", type: "Date", public: true },
      { name: "namespace", type: "String", public: false },
      { name: "channel", type: "LowCardinality(String)", public: true },
      { name: "device", type: "LowCardinality(String)", public: true },
      { name: "sessions", type: "UInt64", public: true },
      { name: "page_views", type: "UInt64", public: true },
      { name: "add_to_cart_sessions", type: "UInt64", public: true },
      { name: "checkout_sessions", type: "UInt64", public: true },
      { name: "purchase_sessions", type: "UInt64", public: true },
      { name: "orders", type: "UInt64", public: true },
      { name: "revenue_usd", type: "Float64", public: true },
    ],
  },
  {
    name: "benchmark_namespace_domain",
    description:
      "The root domain this project is benchmarked as: the one its pages got the most views on in the last 30 days. Query with FINAL.",
    projectScoped: true,
    public: true,
    timestampColumn: null,
    columns: [
      { name: "namespace", type: "String", public: false },
      { name: "root_domain", type: "String", public: true },
      { name: "views_30d", type: "UInt64", public: true },
      { name: "updated_at", type: "DateTime", public: true },
    ],
  },
  {
    name: "consent_events",
    description:
      "Append-only visitor consent decisions for audits and exports.",
    projectScoped: true,
    public: false,
    timestampColumn: "date",
    columns: [
      { name: "namespace", type: "String", public: false },
      { name: "consent_id", type: "String", public: false },
      { name: "date", type: "DateTime64(3)", public: false },
      { name: "id", type: "UUID", public: false },
      { name: "action", type: "String", public: false },
      { name: "consent_mode", type: "String", public: false },
      { name: "policy_version", type: "UInt32", public: false },
      { name: "categories", type: "String", public: false },
      { name: "region", type: "String", public: false },
      { name: "gpc", type: "UInt8", public: false },
      { name: "useragent", type: "String", public: false },
      { name: "referrer", type: "String", public: false },
      { name: "ip_country", type: "String", public: false },
    ],
  },
] as const satisfies readonly AnalyticsQueryTable[];

export const publicAnalyticsQueryTables = analyticsQueryTables
  .filter((table) => table.public)
  .map((table) => {
    return {
      ...table,
      columns: table.columns.filter((column) => column.public),
    };
  });
